/**
 * Points. The only place in the codebase that decides what a round is worth —
 * shared by the offline scorekeeper and the live game so the two can never
 * drift apart.
 */

import type {
  PlannedRound,
  ScoreEntry,
  ScoreRound,
  ScoringConfig,
  ScoringPresetId,
  Seat,
  Standing,
} from '@shared/types/game';

export const SCORING_PRESETS: Record<Exclude<ScoringPresetId, 'eigen'>, ScoringConfig> = {
  /** The most common Dutch variant: exact = 10 + your bid, miss = -2 per trick off. */
  'nl-standaard': {
    exactBase: 10,
    perTrickBid: 1,
    bidSquaredBonus: 0,
    missPenaltyPerTrick: 2,
    alsoScoreTricksOnMiss: false,
  },
  /** Rewards ambitious bids harder. */
  'dubbel-bod': {
    exactBase: 10,
    perTrickBid: 2,
    bidSquaredBonus: 0,
    missPenaltyPerTrick: 2,
    alsoScoreTricksOnMiss: false,
  },
  /** Gentler on young players: a miss costs 1 per trick instead of 2. */
  mild: {
    exactBase: 10,
    perTrickBid: 1,
    bidSquaredBonus: 0,
    missPenaltyPerTrick: 1,
    alsoScoreTricksOnMiss: false,
  },
  /**
   * Exact = 10 + your bid squared, so a big call pays off steeply: 0 → 10,
   * 2 → 14, 4 → 26, 6 → 46. A miss still costs 2 per trick, which makes going
   * for a large bid genuinely risky rather than just better.
   */
  kwadraat: {
    exactBase: 10,
    perTrickBid: 0,
    bidSquaredBonus: 1,
    missPenaltyPerTrick: 2,
    alsoScoreTricksOnMiss: false,
  },
};

export const DEFAULT_SCORING_PRESET: ScoringPresetId = 'nl-standaard';

export function scoringConfigFor(preset: ScoringPresetId): ScoringConfig {
  if (preset === 'eigen') {
    // 'eigen' has no canonical values; callers supply their own and store them
    // on the game. Fall back to the standard so a half-built config still works.
    return { ...SCORING_PRESETS['nl-standaard'] };
  }
  return { ...SCORING_PRESETS[preset] };
}

/**
 * Points for one player in one round.
 *
 * Exact: `exactBase + perTrickBid * bid + bidSquaredBonus * bid²`.
 * Miss:  `-missPenaltyPerTrick * |tricksWon - bid|`, optionally plus a point
 * per trick actually won when the `alsoScoreTricksOnMiss` house rule is on.
 *
 * `bidSquaredBonus` is read as 0 when absent, so a game stored before that field
 * existed rescores to exactly the same number it originally had.
 */
export function scoreRound(bid: number, tricksWon: number, config: ScoringConfig): number {
  assertCount(bid, 'bid');
  assertCount(tricksWon, 'tricksWon');

  if (bid === tricksWon) {
    return config.exactBase + config.perTrickBid * bid + (config.bidSquaredBonus ?? 0) * bid * bid;
  }

  const penalty = -config.missPenaltyPerTrick * Math.abs(tricksWon - bid);
  return config.alsoScoreTricksOnMiss ? penalty + tricksWon : penalty;
}

/** Best achievable score in a round of `cards` cards: bid them all and make them. */
export function bestPossibleRoundScore(cards: number, config: ScoringConfig): number {
  return scoreRound(cards, cards, config);
}

/**
 * Fill in `points` for every entry that has both a bid and a trick count.
 * Returns a new round; the input is not mutated.
 */
export function withScores(round: ScoreRound, config: ScoringConfig): ScoreRound {
  return {
    ...round,
    entries: round.entries.map((entry) => ({
      ...entry,
      points:
        entry.bid === null || entry.tricksWon === null
          ? null
          : scoreRound(entry.bid, entry.tricksWon, config),
    })),
  };
}

/**
 * Cumulative totals per seat after each round, indexed
 * `[roundIndex][seat]` — the shape the standings chart consumes directly.
 * Rounds that are not scored yet contribute nothing.
 */
export function runningTotals(rounds: readonly ScoreRound[], playerCount: number): number[][] {
  const totals = new Array<number>(playerCount).fill(0);

  return rounds.map((round) => {
    for (const entry of round.entries) {
      if (entry.points !== null && entry.seat >= 0 && entry.seat < playerCount) {
        totals[entry.seat] += entry.points;
      }
    }
    return [...totals];
  });
}

/** Final total per seat. */
export function seatTotals(rounds: readonly ScoreRound[], playerCount: number): number[] {
  const history = runningTotals(rounds, playerCount);
  return history.length > 0 ? history[history.length - 1] : new Array(playerCount).fill(0);
}

/**
 * Standings, highest first. Tied players share a placement, and the placement
 * after a tie skips ahead (1, 2, 2, 4) so the numbers still count seats.
 */
export function standings(rounds: readonly ScoreRound[], seats: readonly Seat[]): Standing[] {
  const totals = seatTotals(rounds, seats.length);

  const ordered = seats
    .map((seat) => ({
      seat: seat.seat,
      displayName: seat.displayName,
      total: totals[seat.seat] ?? 0,
      placement: 0,
    }))
    .sort((a, b) => b.total - a.total || a.seat - b.seat);

  let placement = 0;
  let previousTotal: number | null = null;
  ordered.forEach((row, index) => {
    if (previousTotal === null || row.total !== previousTotal) {
      placement = index + 1;
      previousTotal = row.total;
    }
    row.placement = placement;
  });

  return ordered;
}

/**
 * Points still theoretically available to one player from `fromRoundNo` on —
 * the input for "can anyone still catch the leader?".
 */
export function maxRemainingPoints(
  plan: readonly PlannedRound[],
  fromRoundNo: number,
  config: ScoringConfig
): number {
  return plan
    .filter((round) => round.roundNo >= fromRoundNo)
    .reduce((sum, round) => sum + bestPossibleRoundScore(round.cards, config), 0);
}

/** True when a round has a bid and a trick count for every seat. */
export function isRoundComplete(round: ScoreRound): boolean {
  return round.entries.every((entry) => entry.bid !== null && entry.tricksWon !== null);
}

export function sumBids(entries: readonly ScoreEntry[]): number {
  return entries.reduce((sum, entry) => sum + (entry.bid ?? 0), 0);
}

export function sumTricks(entries: readonly ScoreEntry[]): number {
  return entries.reduce((sum, entry) => sum + (entry.tricksWon ?? 0), 0);
}

function assertCount(value: number, label: string): void {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${label} must be a non-negative integer, got ${value}`);
  }
}
